import { t } from '@lingui/core/macro';
import { Fragment, createContext, useMemo } from 'react';

import { type OptionType } from '@/api/common';
import { CheckboxGroup } from '@/components/Form';
import { Text } from '@/components/typography';
import useControlled from '@/hooks/useControlled';
import { cn } from '@/lib/utils';

import ListItem from './ListItem';
import { type ListContextType, type ListProps } from './types';

const SYSTEM_STATUS_ID = 'C99-STATUS';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ListContext = createContext<ListContextType<any>>({
  value: [],
  onChange: () => {},
  selectionMode: 'none',
  disabled: false,
});

const List = <T extends OptionType>({
  defaultValue,
  value: valueFromProps,
  options,
  renderItem,
  selectionMode = 'none',
  isLoading = false,
  isLoadingMore = false,
  disabled = false,
  error,
  onChange: onChangeFromProps,
}: ListProps<T>) => {
  const [value, onChange] = useControlled(valueFromProps, defaultValue, onChangeFromProps);
  const selectedKeys = value ?? [];

  const listOptions = useMemo(() => {
    if (error) {
      return [
        { id: SYSTEM_STATUS_ID, name: t`Unable to load items, please refresh and try again` } as T,
      ];
    }

    if (isLoading) {
      return [{ id: SYSTEM_STATUS_ID, name: t`Loading...` } as T];
    }

    if (isLoadingMore) {
      return options?.concat({ id: SYSTEM_STATUS_ID, name: t`Loading...` } as T);
    }

    return options;
  }, [options, isLoading, isLoadingMore, error]);

  const handleRenderItem = (item: T) => {
    if (item.id === SYSTEM_STATUS_ID) {
      return (
        <ListItem id={item.id} interactive={false}>
          <Text variant="body2" color="grey" italic>
            {item.name}
          </Text>
        </ListItem>
      );
    }

    return renderItem?.(item);
  };

  const handleValueChange = (keys: string[]) => {
    if (selectionMode === 'single') {
      const addedKey = keys.find((key) => !selectedKeys.includes(key));
      onChange(addedKey ? [addedKey] : []);
      return;
    }

    onChange(keys);
  };

  const listContent = (
    <>
      {listOptions?.length ? (
        listOptions.map((item) => <Fragment key={item.id}>{handleRenderItem(item)}</Fragment>)
      ) : (
        <ListItem id={SYSTEM_STATUS_ID} interactive={false}>
          <Text variant="body2" color="grey" italic>
            {t`No items found.`}
          </Text>
        </ListItem>
      )}
    </>
  );

  return (
    <ListContext.Provider value={{ value, onChange, disabled, selectionMode }}>
      {selectionMode === 'none' ? (
        <div role="list" className={cn('flex w-full flex-1 flex-col')}>
          {listContent}
        </div>
      ) : (
        <CheckboxGroup
          className="gap-0"
          value={selectedKeys}
          onValueChange={handleValueChange}
          disabled={disabled}
        >
          {listContent}
        </CheckboxGroup>
      )}
    </ListContext.Provider>
  );
};

List.Item = ListItem;

export default List;
